import React, { useMemo, useState } from "react";
import { Avatar, Box, Paper, Skeleton, Typography } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorState from "../shared/ErrorState";
import SearchField from "../shared/SearchField";
import ActionModal from "../shared/ActionModal";
import CertificatesIconButton from "../Certificates/CertificatesIconButton";
import CertificateCanvas, {
  CERTIFICATE_HEIGHT_PX,
  CERTIFICATE_WIDTH_PX,
} from "../Certificates/CertificateCanvas";
import { useCourseCompletions } from "../../hooks/useCourseCompletions";
import { useCertificateFonts } from "../../hooks/useCertificateFonts";
import { useCertificateOrganisation } from "../../hooks/useCertificateOrganisation";
import type { ApiCertification, ApiCourseCompletion } from "../../services/api/courseProgress";
import { courseCertificate, courseCertificateId } from "../../domain/help/courseCertificate";
import { formatCertificateDate } from "../../domain/certificates/certificateDate";
import {
  MANAGEMENT_AVATAR_FONT_SIZE,
  MANAGEMENT_AVATAR_SIZE,
} from "../../styles/managementShared";
import {
  MANAGEMENT_CARD_HEADER_FONT_SIZE,
  MANAGEMENT_CARD_META_FONT_SIZE,
  MANAGEMENT_CARD_TITLE_FONT_SIZE,
} from "../../styles/managementCardTypography";
import { MANAGEMENT_ACTION_ICON_FONT_SIZE } from "../../styles/managementCardActions";
import { lightTheme } from "../../styles/theme";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";

const GRID_COLUMNS =
  "minmax(0, 2fr) minmax(0, 1.4fr) minmax(0, 1.4fr) minmax(0, 1.1fr) auto";

const HEADER_CELL_SX = {
  fontWeight: 600,
  color: "text.secondary",
  textTransform: "uppercase",
  fontSize: MANAGEMENT_CARD_HEADER_FONT_SIZE,
  letterSpacing: "0.5px",
} as const;

const META_CELL_SX = {
  color: "text.secondary",
  fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
  minWidth: 0,
} as const;

const accountLabel = (accountType: string) => {
  if (accountType === "team") return "Staff";
  if (accountType === "organization") return "Organisation account";
  return accountType;
};

const certificateIdOf = (completion: ApiCourseCompletion) =>
  completion.finishedOn
    ? courseCertificateId(completion.userId, completion.finishedOn)
    : "";

const completedOn = (completion: ApiCourseCompletion) =>
  formatCertificateDate(completion.finishedOn ?? undefined) || "Date not recorded";

const CourseCertificatePreview: React.FC<{
  completion: ApiCourseCompletion;
  onClose: () => void;
}> = ({ completion, onClose }) => {
  useCertificateFonts();
  const { organisation } = useCertificateOrganisation();
  const finishedOn = completion.finishedOn;
  if (!finishedOn) return null;
  const drawn = courseCertificate({
    name: completion.displayName,
    memberId: completion.userId,
    finishedOn,
    organisation,
  });

  return (
    <ActionModal
      open
      onClose={onClose}
      title={`${completion.displayName}'s certificate`}
      maxWidth="md"
      headerStyle="light"
    >
      <Box
        sx={{
          boxShadow: 2,
          mx: "auto",
          maxWidth: `calc((100vh - 260px) * ${CERTIFICATE_WIDTH_PX} / ${CERTIFICATE_HEIGHT_PX})`,
        }}
      >
        <CertificateCanvas
          template={drawn.template}
          award={drawn.award}
          recipient={drawn.recipient}
        />
      </Box>
    </ActionModal>
  );
};

const CompletionRows: React.FC<{
  certification: ApiCertification;
  completions: ApiCourseCompletion[];
  onPreview: (completion: ApiCourseCompletion) => void;
}> = ({ certification, completions, onPreview }) => (
  <Box
    role="table"
    aria-label={`${certification.name} completions`}
    sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}
  >
    <Box
      role="row"
      sx={{
        display: { xs: "none", md: "grid" },
        gridTemplateColumns: GRID_COLUMNS,
        gap: 2,
        px: 3,
        py: 1,
        mb: 1,
      }}
    >
      {["Staff", "Account", "Certificate ID", "Completed"].map((heading) => (
        <Typography key={heading} role="columnheader" sx={HEADER_CELL_SX}>
          {heading}
        </Typography>
      ))}
      <Box role="columnheader" />
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {completions.map((completion) => {
        const certificateId = certificateIdOf(completion);
        return (
          <Paper
            key={`${completion.accountType}:${completion.userId}`}
            role="row"
            elevation={0}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: GRID_COLUMNS },
              gap: { xs: 0.5, md: 2 },
              px: { xs: 2, md: 3 },
              py: 2,
              alignItems: "center",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
                transform: "translateY(-1px)",
              },
            }}
          >
            <Box role="cell" sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
              <Avatar
                sx={{
                  bgcolor: getAvatarColor(completion.displayName),
                  width: MANAGEMENT_AVATAR_SIZE,
                  height: MANAGEMENT_AVATAR_SIZE,
                  fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                }}
              >
                {getInitials(completion.displayName)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 500, fontSize: MANAGEMENT_CARD_TITLE_FONT_SIZE }}>
                  {completion.displayName}
                </Typography>
                <Typography sx={{ display: { md: "none" }, ...META_CELL_SX }}>
                  {certification.awardName}
                </Typography>
              </Box>
            </Box>
            <Typography role="cell" sx={META_CELL_SX}>
              {accountLabel(completion.accountType)}
            </Typography>
            <Typography role="cell" sx={META_CELL_SX}>
              {certificateId || "Not recorded"}
            </Typography>
            <Typography role="cell" sx={META_CELL_SX}>
              {completedOn(completion)}
            </Typography>
            <Box role="cell" sx={{ display: "flex", alignItems: "center" }}>
              {completion.finishedOn ? (
                <CertificatesIconButton
                  label={`Preview ${completion.displayName}'s certificate`}
                  onClick={() => onPreview(completion)}
                >
                  <VisibilityIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                </CertificatesIconButton>
              ) : null}
            </Box>
          </Paper>
        );
      })}
    </Box>
  </Box>
);

const CertificationCard: React.FC<{
  certification: ApiCertification;
  onPreview: (completion: ApiCourseCompletion) => void;
}> = ({ certification, onPreview }) => {
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const shown = useMemo(
    () =>
      certification.completions.filter((completion) => {
        if (!query) return true;
        return [completion.displayName, accountLabel(completion.accountType), certificateIdOf(completion)]
          .join(" ")
          .toLowerCase()
          .includes(query);
      }),
    [certification.completions, query],
  );
  const completedCount = certification.completions.length;

  return (
    <Paper
      component="section"
      aria-label={certification.name}
      elevation={0}
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 }, borderBottom: 1, borderColor: "divider" }}>
        <Typography component="h2" sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
          {certification.name}
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.5 }}>
          Award: {certification.awardName}
          {completedCount > 0 ? ` · ${completedCount} completed` : ""}
        </Typography>
      </Box>

      {completedCount > 0 && (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: "divider" }}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search staff by name or certificate ID"
            sx={{ width: "100%" }}
          />
        </Box>
      )}

      {completedCount === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
          <EmojiEventsIcon sx={{ fontSize: 64, color: "text.secondary", opacity: 0.5, mb: 2 }} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            No one has completed this certification yet
          </Typography>
          <Typography variant="body2">
            Staff earn a {certification.awardName} when they finish every lesson.
          </Typography>
        </Box>
      ) : shown.length === 0 ? (
        <Typography sx={{ px: { xs: 2, sm: 3 }, py: 4, color: "text.secondary" }}>
          No staff match that search.
        </Typography>
      ) : (
        <CompletionRows
          certification={certification}
          completions={shown}
          onPreview={onPreview}
        />
      )}
    </Paper>
  );
};

/** Certifications for this organization, and the staff who earned each award. */
const TeamCertifications: React.FC = () => {
  const { data, isLoading, isError, refetch } = useCourseCompletions();
  const [preview, setPreview] = useState<ApiCourseCompletion | null>(null);

  if (isLoading) {
    return (
      <Box aria-label="Loading certifications" role="status">
        <Skeleton variant="rounded" height={220} />
      </Box>
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="Couldn't load certifications"
        description="Check your connection and try again."
        imageSrc="/icons/lost.webp"
        imageAlt="Certifications unavailable"
        onRetry={() => void refetch()}
      />
    );
  }

  const certifications = data?.certifications ?? [];
  if (certifications.length === 0) {
    return (
      <Typography sx={{ color: "text.secondary", py: 4 }}>
        No certifications are available yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {certifications.map((certification) => (
        <CertificationCard
          key={certification.certificationId}
          certification={certification}
          onPreview={setPreview}
        />
      ))}
      {preview?.finishedOn ? (
        <CourseCertificatePreview completion={preview} onClose={() => setPreview(null)} />
      ) : null}
    </Box>
  );
};

export default TeamCertifications;
